import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StickyNote, Loader2, Save, X, Pin, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const COLOR_PALETTE = [
  { v: "yellow", chip: "bg-yellow-400" },
  { v: "green", chip: "bg-emerald-500" },
  { v: "blue", chip: "bg-sky-500" },
  { v: "pink", chip: "bg-pink-500" },
  { v: "purple", chip: "bg-violet-500" },
  { v: "orange", chip: "bg-orange-500" },
];

const FAB_POS_KEY = "quick_note_fab_pos_v1";

/**
 * Floating Action Button "Appunto al volo" — visibile in tutte le aree dello staff.
 * Apre un dialog rapido per scrivere un appunto e salvarlo senza cambiare pagina.
 * Trascinabile con il mouse (posizione persistita in localStorage).
 */
export function QuickNoteFab() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [color, setColor] = useState("green");
  const [pinned, setPinned] = useState(false);
  const [linkedType, setLinkedType] = useState("");
  const [linkedLabel, setLinkedLabel] = useState("");
  const [busy, setBusy] = useState(false);

  // --- Drag & posizione ---
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(FAB_POS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p.x === "number" && typeof p.y === "number") return p;
      }
    } catch { /* noop */ }
    return null; // null = posizione default (bottom-right via Tailwind)
  });
  const dragRef = useRef({ dragging: false, moved: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const btnRef = useRef(null);

  // Sincronizza con il server: al mount, prova a leggere la posizione dal profilo
  // utente. Se trovata, vince sul localStorage (cross-device).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/ui-prefs");
        const serverPos = data?.quick_note_fab_pos;
        if (!cancelled && serverPos && typeof serverPos.x === "number" && typeof serverPos.y === "number") {
          setPos(serverPos);
          try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(serverPos)); } catch { /* noop */ }
        }
      } catch { /* utente non autenticato o endpoint non disponibile: ok, usiamo localStorage */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounce salvataggio lato server: 800ms dopo l'ultimo cambio di posizione
  const saveTimerRef = useRef(null);
  const syncPosToServer = (next) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await api.put("/me/ui-prefs", { quick_note_fab_pos: next });
      } catch { /* silenzioso: l'utente ha comunque localStorage */ }
    }, 800);
  };

  const clampToViewport = (x, y) => {
    const w = btnRef.current?.offsetWidth || 120;
    const h = btnRef.current?.offsetHeight || 48;
    const maxX = Math.max(0, window.innerWidth - w - 4);
    const maxY = Math.max(0, window.innerHeight - h - 4);
    return { x: Math.min(Math.max(4, x), maxX), y: Math.min(Math.max(4, y), maxY) };
  };

  const onMouseDown = (e) => {
    // Solo tasto sinistro
    if (e.button !== 0) return;
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      dragging: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    e.preventDefault();
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    if (!t) return;
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      dragging: true,
      moved: false,
      startX: t.clientX,
      startY: t.clientY,
      offsetX: t.clientX - rect.left,
      offsetY: t.clientY - rect.top,
    };
  };

  useEffect(() => {
    const move = (clientX, clientY) => {
      const d = dragRef.current;
      if (!d.dragging) return;
      const dx = Math.abs(clientX - d.startX);
      const dy = Math.abs(clientY - d.startY);
      if (!d.moved && (dx > 5 || dy > 5)) d.moved = true;
      if (d.moved) {
        const next = clampToViewport(clientX - d.offsetX, clientY - d.offsetY);
        setPos(next);
      }
    };
    const onMouseMove = (e) => move(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (t) move(t.clientX, t.clientY);
    };
    const endDrag = () => {
      const d = dragRef.current;
      if (d.dragging && d.moved) {
        try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(pos)); } catch { /* noop */ }
        if (pos) syncPosToServer(pos);
      }
      d.dragging = false;
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", endDrag);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [pos]);

  // Riposiziona se la finestra viene ridimensionata e l'icona finisce fuori schermo
  useEffect(() => {
    if (!pos) return;
    const onResize = () => setPos((p) => p ? clampToViewport(p.x, p.y) : p);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  const reset = () => {
    setTitle(""); setBody(""); setColor("green"); setPinned(false);
    setLinkedType(""); setLinkedLabel("");
  };

  const save = async () => {
    if (!body.trim()) { toast.error("Scrivi il contenuto dell'appunto"); return; }
    setBusy(true);
    try {
      await api.post("/notes", {
        title, body, color, pinned,
        linked_type: linkedType || null,
        linked_label: linkedLabel || null,
      });
      toast.success("Appunto salvato");
      setOpen(false); reset();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        data-testid="quick-note-fab"
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onClick={(e) => {
          // Apri il dialog SOLO se non è stato un drag
          if (dragRef.current.moved) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          setOpen(true);
        }}
        onContextMenu={(e) => {
          // Click destro → ripristina posizione di default (bottom-right)
          e.preventDefault();
          if (pos) {
            setPos(null);
            try { localStorage.removeItem(FAB_POS_KEY); } catch { /* noop */ }
            // Sincronizza il reset sul server (rimuove la preferenza salvata)
            api.put("/me/ui-prefs", { quick_note_fab_pos: null }).catch(() => { /* silenzioso */ });
            toast.success("Icona riportata in posizione predefinita (sincronizzato su tutti i dispositivi)");
          } else {
            toast.info("L'icona è già in posizione predefinita");
          }
        }}
        title="Appunto al volo — trascina per spostare · tasto destro per ripristinare"
        style={pos ? { left: `${pos.x}px`, top: `${pos.y}px`, bottom: "auto", right: "auto" } : undefined}
        className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl rounded-full pl-3.5 pr-4 py-3 transition-shadow hover:scale-105 cursor-grab active:cursor-grabbing select-none touch-none"
      >
        <StickyNote className="h-5 w-5" />
        <span className="text-sm font-semibold hidden sm:inline">Appunto</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); reset(); } }}>
        <DialogContent className="max-w-md" data-testid="quick-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5 text-emerald-600" /> Appunto rapido
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium block mb-1">Titolo (facoltativo)</label>
              <Input
                data-testid="qn-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Es. Promemoria…"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Contenuto <span className="text-red-500">*</span></label>
              <Textarea
                data-testid="qn-body"
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Scrivi qui il tuo appunto…"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1.5">Colore</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    data-testid={`qn-color-${c.v}`}
                    onClick={() => setColor(c.v)}
                    className={`w-7 h-7 rounded-full ${c.chip} ring-2 transition ${color === c.v ? "ring-slate-900 ring-offset-2" : "ring-transparent"}`}
                    title={c.v}
                  />
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="qn-pinned"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
                className="h-4 w-4"
              />
              <Pin className="h-3.5 w-3.5 text-amber-600" />
              <span>Fissa in alto</span>
            </label>
            <div className="border-t border-slate-200 pt-2">
              <label className="text-xs font-medium block mb-1 flex items-center gap-1">
                <Link2 className="h-3 w-3 text-slate-500" /> Collegato a (facoltativo)
              </label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  data-testid="qn-linked-type"
                  className="h-9 rounded-md border border-slate-300 px-2 text-xs bg-white"
                  value={linkedType}
                  onChange={(e) => setLinkedType(e.target.value)}
                >
                  <option value="">Nessuno</option>
                  <option value="client">Cliente</option>
                  <option value="policy">Polizza</option>
                  <option value="quote">Preventivo</option>
                </select>
                <Input
                  data-testid="qn-linked-label"
                  className="col-span-2 h-9 text-xs"
                  placeholder="Es. Mario Rossi · POL-2026-001"
                  value={linkedLabel}
                  onChange={(e) => setLinkedLabel(e.target.value)}
                  disabled={!linkedType}
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-500 pt-1 border-t">
              💡 Trovi tutti i tuoi appunti su{" "}
              <button
                type="button"
                onClick={() => { setOpen(false); reset(); nav("/notes"); }}
                className="text-emerald-700 hover:underline font-semibold"
                data-testid="qn-go-to-notes"
              >
                Appunti rapidi →
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>
              <X className="h-4 w-4 mr-1" /> Annulla
            </Button>
            <Button
              data-testid="qn-save"
              onClick={save}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva appunto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
